import React, { useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";

const ContactUs = () => {
    // 1. State for form data
    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        message: ""
    });

    // 2. State for errors and submission status
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null); // 'success' or 'error'

    // 3. Handle Input Change
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors({
                ...errors,
                [name]: ""
            });
        }
    };

    // 4. Validation Logic
    const validate = () => {
        let tempErrors = {};
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const phoneRegex = /^[0-9]{10}$/; // Assumes 10 digit number

        if (!formData.firstName.trim()) tempErrors.firstName = "First name is required";
        if (!formData.lastName.trim()) tempErrors.lastName = "Last name is required";
        
        if (!formData.email) {
            tempErrors.email = "Email is required";
        } else if (!emailRegex.test(formData.email)) {
            tempErrors.email = "Invalid email format";
        }

        if (!formData.phone) {
            tempErrors.phone = "Phone number is required";
        } else if (!phoneRegex.test(formData.phone)) {
            tempErrors.phone = "Invalid 10-digit phone number";
        }

        if (!formData.message.trim()) tempErrors.message = "Message is required";

        setErrors(tempErrors);
        return Object.keys(tempErrors).length === 0;
    };

    // 5. Handle Submit
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (validate()) {
            setIsSubmitting(true);
            setSubmitStatus(null);

            // Create the payload expected by your backend (matching enquiryUtils structure)
            const apiPayload = {
                name: `${formData.firstName} ${formData.lastName}`, // Backend expects 'name'
                email: formData.email,
                mobile: formData.phone, // Backend expects 'mobile'
                message: formData.message
            };

            try {
                // FIXED: Direct URL to production to solve connection refused error
                const API_URL = 'http://localhost:3000/v1/enquiry/save_enquiry' || "https://policysaath.com/v1/enquiry/save_enquiry";
                
                const response = await fetch(API_URL, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(apiPayload),
                });

                const data = await response.json();

                if (response.ok) {
                    setSubmitStatus("success");
                    setFormData({ firstName: "", lastName: "", email: "", phone: "", message: "" }); // Reset form
                } else {
                    console.error("Server Error:", data.message);
                    setSubmitStatus("error");
                }
            } catch (error) {
                console.error("Error submitting form:", error);
                setSubmitStatus("error");
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    return (
        <>
            <Header />

            <div className="contact-us-page">

                {/* Top Banner Section */}
                <section className="contact-hero-banner">
                    <div className="banner-overlay"></div>
                    <div className="banner-content">
                        <h1>Contact Us</h1>
                        <p>Get in touch with our team to discuss how we can help transform your insurance business.</p>
                    </div>
                </section>

                {/* Consultant Section */}
                <section className="consultant-section">
                    <div className="container">
                        <div className="consultant-grid">

                            {/* Left Side: Content */}
                            <div className="consultant-info">
                                <h2>Connect With Our Experts to Achieve Your Digital Vision</h2>
                                <div className="red-divider"></div>

                                <div className="contact-details">
                                    <div className="detail-item">
                                        <h3>Call Us -</h3>
                                        <a href="tel:+91 9136356555" className="highlight-teal">
                                            +91 9136356555
                                        </a>
                                    </div>

                                    <div className="detail-item">
                                        <h3>Email Us -</h3>
                                        <a href="mailto:policysaath@gmail.com" className="highlight-teal">
                                            policysaath@gmail.com
                                        </a>
                                    </div>
                                </div>
                            </div>

                            {/* Right Side: Form */}
                            <div className="consultant-form-wrapper">
                                <div className="form-card">
                                    <h3>Talk to an Expert</h3>
                                    
                                    <form onSubmit={handleSubmit} noValidate>
                                        <div className="form-row">
                                            <div style={{width: '100%'}}>
                                                <input 
                                                    type="text" 
                                                    name="firstName"
                                                    placeholder="First Name*" 
                                                    value={formData.firstName}
                                                    onChange={handleChange}
                                                    className={errors.firstName ? "input-error" : ""}
                                                />
                                                {errors.firstName && <span style={{color:'red', fontSize:'12px'}}>{errors.firstName}</span>}
                                            </div>

                                            <div style={{width: '100%'}}>
                                                <input 
                                                    type="text" 
                                                    name="lastName"
                                                    placeholder="Last Name*" 
                                                    value={formData.lastName}
                                                    onChange={handleChange}
                                                    className={errors.lastName ? "input-error" : ""}
                                                />
                                                {errors.lastName && <span style={{color:'red', fontSize:'12px'}}>{errors.lastName}</span>}
                                            </div>
                                        </div>

                                        <div className="form-full">
                                            <input 
                                                type="email" 
                                                name="email"
                                                placeholder="Email Address*" 
                                                value={formData.email}
                                                onChange={handleChange}
                                                className={errors.email ? "input-error" : ""}
                                            />
                                            {errors.email && <span style={{color:'red', fontSize:'12px'}}>{errors.email}</span>}
                                        </div>

                                        <div className="form-full">
                                            <input 
                                                type="tel" 
                                                name="phone"
                                                placeholder="Phone*" 
                                                value={formData.phone}
                                                onChange={handleChange}
                                                maxLength="10"
                                                className={errors.phone ? "input-error" : ""}
                                            />
                                            {errors.phone && <span style={{color:'red', fontSize:'12px'}}>{errors.phone}</span>}
                                        </div>

                                        <div className="form-full">
                                            <textarea 
                                                name="message"
                                                placeholder="How can we help you?*" 
                                                rows="2"
                                                value={formData.message}
                                                onChange={handleChange}
                                                className={errors.message ? "input-error" : ""}
                                            ></textarea>
                                            {errors.message && <span style={{color:'red', fontSize:'12px'}}>{errors.message}</span>}
                                        </div>

                                        {/* Status Messages */}
                                        {submitStatus === "success" && (
                                            <div style={{color: "green", marginBottom: "10px", textAlign: "center"}}>
                                                Message sent successfully! We will contact you soon.
                                            </div>
                                        )}
                                        {submitStatus === "error" && (
                                            <div style={{color: "red", marginBottom: "10px", textAlign: "center"}}>
                                                Something went wrong. Please try again later.
                                            </div>
                                        )}

                                        <div className="form-submit">
                                            <button type="submit" disabled={isSubmitting}>
                                                {isSubmitting ? "Sending..." : "Book a Demo"}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>

                        </div>
                    </div>
                </section>

                {/* Presence Section */}
                <section className="presence-section">
                    <div className="container">
                        <div className="presence-content">
                            <h2>Serving Insurance Professionals Across Regions</h2>
                            <div className="red-divider"></div>
                            <p>
                                Our understanding of both established and growing insurance markets,
                                supported by a strong local presence, allows us to work closely with
                                agents and ensure smooth communication at every step.
                            </p>
                        </div>
                    </div>
                </section>

            </div>

            <Footer />
        </>
    );
};

export default ContactUs;